import { LinkButton } from '@euler/app/components/LinkButton';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBottomActionScroll } from '@euler/functions/useBottomActionScroll';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useBehaviorSubject } from '@euler/utils/hooks';
import BottomSheet, { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform, ScrollView, ScrollViewProps } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject, skip } from 'rxjs';
import { OrderDetailContent } from './OrderDetailContent';

const kHandleHeight = 24;

export const OrderDetailBottomSheet = memo(
  ({
    visible$,
    ...props
  }: {
    visible$: BehaviorSubject<boolean>;
    hideEstimatedFinishTime?: boolean;
    hideDashboardInfo?: boolean;
    hideSignInfo?: boolean;
  }) => {
    const {
      windowDimensions: { height },
      navBarHeight,
    } = useSystemMetrics();
    const { rpx } = useResponsiveContext();
    const bottomSheetRef = useRef<BottomSheet>(null);
    const animatedIndex = useSharedValue(0);
    const animatedPosition = useSharedValue(0);
    const footerHeight = useFooterHeight();
    const minSnapPoint = footerHeight;
    const maxSnapPoint = (height - navBarHeight) * 1;
    const snapPoints = useMemo(
      () => [minSnapPoint, maxSnapPoint * 0.7, maxSnapPoint],
      [minSnapPoint, maxSnapPoint],
    );

    const [visible] = useBehaviorSubject(visible$);

    useEffect(() => {
      const sub = visible$.pipe(skip(1)).subscribe(v => {
        if (v) {
          bottomSheetRef?.current?.snapToIndex(2);
        } else {
          bottomSheetRef.current?.close();
        }
      });
      return () => sub.unsubscribe();
    }, [visible$]);

    const { scrollY, onScroll } = useBottomActionScroll();

    const closeButtonStyle = useAnimatedStyle(() => {
      return {
        opacity: interpolate(scrollY.value, [0, maxSnapPoint / 3], [1, 0]),
      };
    });

    const onClose = useCallback(() => {
      visible$.next(false);
    }, [visible$]);

    return (
      <BottomSheet
        ref={bottomSheetRef}
        snapPoints={snapPoints}
        animatedIndex={animatedIndex}
        animatedPosition={animatedPosition}
        index={-1}
        css={`
          background-color: #fff;
          box-shadow: 0 -1px ${rpx(visible ? 15 : 0)}px rgba(0, 0, 0, 0.5);
          elevation: 3;
        `}
        style={{
          elevation: 3,
        }}
      >
        <Content onScroll={onScroll} {...props} />
        <Animated.View
          style={closeButtonStyle}
          css={`
            position: absolute;
            right: ${rpx(15)}px;
            top: ${rpx(15)}px;
          `}
        >
          <LinkButton
            text="关闭"
            textStyle={{ fontSize: rpx(16) }}
            onPress={onClose}
          />
        </Animated.View>
      </BottomSheet>
    );
  },
);

const Content = memo(
  ({ onScroll, ...props }: { onScroll: ScrollViewProps['onScroll'] }) => {
    const { bottom } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const ScrollComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    return (
      <ScrollComponent
        onScroll={onScroll}
        contentContainerStyle={{
          paddingBottom: bottom + rpx(56),
        }}
      >
        <OrderDetailContent {...props} />
      </ScrollComponent>
    );
  },
);

const useFooterHeight = () => {
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  return rpx(56) + insets.bottom + kHandleHeight;
};
